import { useState, useEffect } from 'react';
import { XMarkIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import api from '../services/api';

const STATUS_COLORS = {
  PENDING_SLICING: 'bg-yellow-100 text-yellow-800',
  READY_TO_PRINT: 'bg-blue-100 text-blue-800',
  PRINTING: 'bg-indigo-100 text-indigo-800',
  COMPLETED: 'bg-green-100 text-green-800',
  FAILED: 'bg-red-100 text-red-800',
  ON_HOLD: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-slate-100 text-slate-800',
};

const formatStatus = (s) => s.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '-';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

export default function EditJobModal({ quoteId, onClose, onSuccess }) {
  const [machines, setMachines] = useState([]);
  const [existingJobs, setExistingJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    targetMachineId: '',
    sequenceMode: 'BATCH',
    reslice: false,
    instructions: '',
  });
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMachines();
    fetchExistingJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMachines = async () => {
    try {
      const response = await api.get('/auth/slicer-settings/machines');
      setMachines(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch machines:', err);
    }
  };

  const fetchExistingJobs = async () => {
    try {
      setLoadingJobs(true);
      const response = await api.get(`/auth/print-queue/quote/${quoteId}`);
      const jobs = response.data.data || [];
      setExistingJobs(jobs);
      // Pre-fill form from first job
      if (jobs.length > 0) {
        const first = jobs[0];
        setFormData(prev => ({
          ...prev,
          targetMachineId: first.targetMachine?._id || first.targetMachine || '',
          sequenceMode: first.sequenceMode || 'BATCH',
          instructions: first.instructions || '',
        }));
      }
    } catch (err) {
      console.error('Failed to fetch existing jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const allEditable = existingJobs.length > 0 && existingJobs.every(
    j => ['READY_TO_PRINT', 'PENDING_SLICING'].includes(j.status)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.targetMachineId) {
      setError('Target Machine is required.');
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append('targetMachineId', formData.targetMachineId);
      data.append('sequenceMode', formData.sequenceMode);
      if (formData.reslice) data.append('reslice', 'true');
      if (formData.instructions) data.append('instructions', formData.instructions);
      if (file) data.append('file', file);

      await api.put(`/auth/print-queue/quote/${quoteId}/edit`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to edit jobs');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Edit Print Jobs</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
          )}

          {/* Existing jobs that will be replaced */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Jobs that will be deleted & recreated</h4>
            {loadingJobs ? (
              <div className="text-sm text-gray-500 py-2">Loading jobs...</div>
            ) : existingJobs.length === 0 ? (
              <div className="text-sm text-gray-500 py-2">No jobs found for this quote.</div>
            ) : (
              <div className="border border-gray-200 rounded-md divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {existingJobs.map((job) => {
                  const editable = ['READY_TO_PRINT', 'PENDING_SLICING'].includes(job.status);
                  return (
                    <div key={job._id} className={`flex items-center justify-between px-3 py-2 text-sm ${!editable ? 'bg-red-50' : ''}`}>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">Plate {job.plateIndex}</span>
                        <span className="text-gray-400">&middot;</span>
                        <span className="text-gray-500">{formatDuration(job.printTimeSeconds)}</span>
                        <span className="text-gray-400">&middot;</span>
                        <span className="text-gray-500">Qty {job.quantityTotal}</span>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[job.status] || 'bg-gray-100 text-gray-600'}`}>
                        {formatStatus(job.status)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            {!loadingJobs && existingJobs.length > 0 && !allEditable && (
              <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                <ExclamationTriangleIcon className="h-4 w-4 shrink-0 mt-0.5" />
                <span>Some jobs have already started and cannot be edited. Only jobs in Ready to Print or Pending Slicing status can be replaced.</span>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Machine *</label>
              <select
                value={formData.targetMachineId}
                onChange={(e) => setFormData({ ...formData, targetMachineId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                required
              >
                <option value="">Select a machine...</option>
                {machines.map((m) => (
                  <option key={m._id} value={m._id}>
                    {m.name} ({m.uid})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sequence Mode *</label>
              <select
                value={formData.sequenceMode}
                onChange={(e) => setFormData({ ...formData, sequenceMode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="BATCH">Batch (by plate)</option>
                <option value="KIT">Kit (by unit)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Production 3MF File</label>
              <input
                type="file"
                accept=".3mf"
                onChange={(e) => setFile(e.target.files[0] || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
              />
            </div>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.reslice}
                onChange={(e) => setFormData({ ...formData, reslice: e.target.checked })}
                className="h-4 w-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">Re-slice uploaded 3MF for accurate print times</span>
            </label>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
              <textarea
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                maxLength={2000}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                placeholder="Operator notes..."
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || loadingJobs || !allEditable || existingJobs.length === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 flex items-center"
              >
                {loading && <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />}
                {loading ? 'Saving...' : 'Edit & Recreate Jobs'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
